// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Placing the machine against the player instead of against the origin (M5-33).
//
// WHY THIS FILE EXISTS
// --------------------
// Every placement in this product used to be measured from the tracking origin. `defaultPlacement`
// puts the cabinet 1.1 m along −Z and its own comment says "a player standing at the origin" —
// which nothing enforces and which is usually false. In a `local-floor` space the origin is
// wherever room setup or the last recentre left it, and the WebXR spec explicitly permits a runtime
// that cannot find a floor to invent one. A player who is three metres from that invented origin
// gets a pinball machine three metres away, and the control they reach for — Recenter — sent it
// back to the origin rather than to them.
//
// **And without a saved space the ritual could never finish at all.** The reticle is drawn where a
// surface probe says the room is; on a headset with no Space Setup there is no room, so the probe
// answers "nothing" on every frame for the whole session. The ritual stayed in `scanning`, the
// pinch did nothing, and the machine stayed at the origin. 06 §3 promises placement works "in a
// room the developer has never seen", and a room nobody has scanned is the commonest such room.
//
// THREE ANSWERS, AND WHY THEY ARE ALL HERE
// ----------------------------------------
// 1. **`levelForward`** — which way the player is facing, flattened. Every other function needs it
//    and getting it wrong by a sign puts the machine behind them.
// 2. **`floorAim`** — where the player's gaze meets the floor, for when there is no room to probe.
//    This is exactly what a hit test against a flat floor would have returned, so the ritual is
//    degraded rather than unfinishable, and the player never learns there was a difference.
// 3. **`clampToReach`** — a cabinet the player can actually touch. A surface probe will happily
//    report the floor across the room, and a restored anchor describes a room that may have been
//    rearranged since. Both end with a machine that is visible and unplayable, which is the worst
//    of the two failure modes: a machine that is *missing* sends the player to the wrist menu, and
//    a machine that is merely far away looks like it is working.
//
// **The reach cap is derived, not chosen.** A player stands at a pinball machine with their hands
// on the lockdown bar, so what has to be within arm's reach is the cabinet's *near edge* — never
// its centre, which is where a `Placement` actually is. Capping the centre at a fixed distance
// would be right for a full-size body and wrong for every other one: at tabletop scale the same
// cap leaves the near edge 1.23 m away, out of reach of the buttons the player is being asked to
// press. So the cap is `arm's reach + half the body`, and it falls out at 1.50 m for a standard
// 1.295 m cabinet — the same number the chess client arrived at from the other direction.

import { yawFacing, type Placement } from './placement'
import type { SurfaceHit } from './placementRitual'

/**
 * How far a standing player's hand goes, metres.
 *
 * 0.85 — fingertips at a comfortable forward reach, not a stretch. Anthropometric forward
 * functional reach for an adult is 0.7–0.8 m at the shoulder; the extra allows the small step
 * forward a player makes without thinking about it, which is the difference between "I have to
 * move" and "it is right there".
 */
export const ARM_REACH = 0.85

/**
 * The gap between the player's head and the near edge of the cabinet, metres.
 *
 * 0.20. It was 0.45 — "where you stand at a pinball machine, close enough that the lockdown bar is
 * under your hands and far enough that the backbox is not against your face", and the number that
 * reproduced the documented 1.1 m stand-off (0.45 + 1.295 / 2 = 1.0975). The product owner
 * measured it on a Quest 3 on 14 Sep 2026, standing at a cabinet Bring it to you had just placed
 * at exactly 0.450 m: *"machine should be about 20 cm (currently shows about 50 cm) from user
 * location … place machine beginning edge of machine about 20 cm in front of user."* The head is
 * above the hips, not in front of them, so 0.20 m from the eyes is the bar against the body — how
 * a person actually stands at a pinball machine. The stand-off is now 0.8475 m for a standard
 * body, and the gap is still the part that must not scale: a tabletop cabinet gets the same
 * 0.20 m to a much smaller body. `BODY_CLEARANCE` (0.15) stays under it, so `reachRange` still
 * admits the placement. Mirrored by hand in `ViewerRay.cs`.
 */
export const PLAYER_GAP = 0.2

/**
 * How much of the cabinet may stand between the player and the floor spot, metres.
 *
 * The floor under a player's own feet is a legal surface probe result and a terrible placement: a
 * player looking down gets a cabinet on top of them. The near edge is kept at least this far away,
 * which is a body's worth of clearance and nothing more.
 */
export const BODY_CLEARANCE = 0.15

/**
 * The steepest a gaze ray may be and still be treated as level, as a downward component.
 *
 * A ray a fraction below the horizon meets the floor at an absurd distance — hundreds of metres
 * for a ray 0.01 below level — and flinging the reticle to the horizon every time the player looks
 * up is worse than not answering. Rays shallower than this take the fixed-distance path instead,
 * which always has somewhere sensible to be.
 */
export const AIM_MIN_DOWN = 0.08

/** The furthest a gaze-ray answer may land before the reach clamp gets to it, metres. */
export const AIM_MAX_DISTANCE = 3

/** A direction on the room's floor plane. Unit length unless it came from nothing. */
export interface LevelDirection {
  readonly x: number
  readonly z: number
}

/**
 * Which way the player is facing, with the pitch taken out.
 *
 * **`(0, −1)` when there is nothing to flatten**, because a head at yaw 0 looks down −Z: that is
 * the convention the whole XR layer uses, and it is the one `defaultPlacement` was written
 * against. A player looking straight up or straight down has no horizontal facing at all, and
 * "carry on as though they were looking ahead" is the only answer that does not move the machine
 * somewhere arbitrary.
 */
export function levelForward(fx: number, fz: number): LevelDirection {
  const length = Math.hypot(fx, fz)
  if (!Number.isFinite(length) || length < 1e-6) return { x: 0, z: -1 }
  return { x: fx / length, z: fz / length }
}

/** Within this of a room axis, a first placement is squared to the axis, radians (10°). */
export const ROOM_SNAP = (10 * Math.PI) / 180

/**
 * The head's level forward, squared onto the nearest room axis when it is within `ROOM_SNAP` of one
 * (the owner, 26 Sep 2026: the machine "should be straight on to user").
 *
 * A first placement stood the machine along the gaze at that instant, and a gaze is never quite
 * straight: the build-98 log had the machine at −175.4°, 4.6° off, and Build and Test inherit the
 * play machine's square. The room's axes are the recentre's — the Quest makes −z "straight ahead"
 * wherever the player faced when they recentred — so a gaze that is nearly along one is taken to
 * mean it. A player turned further than `ROOM_SNAP` is facing something on purpose and keeps the
 * gaze.
 */
export function squareToRoom(fx: number, fz: number): LevelDirection {
  const d = levelForward(fx, fz)
  const angle = Math.atan2(d.x, -d.z)
  const quarter = Math.round(angle / (Math.PI / 2))
  if (Math.abs(angle - quarter * (Math.PI / 2)) > ROOM_SNAP) return d
  const axes: readonly LevelDirection[] = [
    { x: 0, z: -1 },
    { x: 1, z: 0 },
    { x: 0, z: 1 },
    { x: -1, z: 0 },
  ]
  return axes[((quarter % 4) + 4) % 4] as LevelDirection
}

/**
 * A forward vector rebuilt from a yaw and a pitch, for callers that carry no vector.
 *
 * **The last resort, not the normal path.** Where a real orientation is in hand the vector should
 * be taken from it — see `xrFrameLoop.headForward` — because reconstructing one means agreeing
 * with whichever convention the yaw was extracted under. This is that convention, written down
 * once: `headYaw` returns `atan2(fx, −fz)` and `tilt` returns `asin(fy)`, so a head at yaw θ and
 * pitch φ is looking along `(sin θ cos φ, sin φ, −cos θ cos φ)`. A head at yaw 0 looks down −Z,
 * which is the same statement `comfort.ts` makes when it says a head looks along `(sin θ, −cos θ)`.
 */
export function forwardFromYaw(yaw: number, pitch: number): { x: number; y: number; z: number } {
  const level = Math.cos(pitch)
  return { x: Math.sin(yaw) * level, y: Math.sin(pitch), z: -Math.cos(yaw) * level }
}

/**
 * How far the cabinet's *centre* may be from the player, given how big it is.
 *
 * Both ends derived from the body rather than typed: `max` puts the near edge at arm's reach and
 * `min` keeps the near edge off the player. A tabletop cabinet is closer at both ends because it
 * is smaller, which is the whole reason this is a function.
 */
export function reachRange(scale: number, cabinetLength: number): { min: number; max: number } {
  const halfDepth = (cabinetLength * scale) / 2
  return { min: halfDepth + BODY_CLEARANCE, max: halfDepth + ARM_REACH }
}

/**
 * Where the machine's centre goes for a player standing at `(headX, headZ)` facing `forward`.
 *
 * `gap` is the space between the player and the near edge — `PLAYER_GAP` for a machine to play,
 * and the bench's own, smaller figure for a machine to work over (`testFlip.ts`).
 */
export function standOffFor(scale: number, cabinetLength: number, gap: number = PLAYER_GAP): number {
  return (cabinetLength * scale) / 2 + gap
}

/**
 * A placement that stands the machine in front of *this* player.
 *
 * The head-relative replacement for `defaultPlacement`, and the answer to "recenter does nothing".
 * The yaw is computed rather than taken as zero: a machine placed in front of a player facing
 * north-east and left at yaw 0 has its side panel to them, which reads as a placement that half
 * worked.
 *
 * `headY` is deliberately **not** used for the machine's height. A floor that cannot be believed
 * is the reason this function is being called on some paths, and `y` is the one number a broken
 * floor poisons; the caller decides whether the machine stands on the floor or hangs off the head,
 * because only the caller knows which of those it is looking at.
 */
export function placementInFrontOf(
  headX: number,
  headZ: number,
  forward: LevelDirection,
  scale: number,
  cabinetLength: number,
  floorY = 0,
  gap: number = PLAYER_GAP,
): Placement {
  const distance = standOffFor(scale, cabinetLength, gap)
  const x = headX + forward.x * distance
  const z = headZ + forward.z * distance
  return { x, y: floorY, z, yaw: yawFacing(x, z, headX, headZ), scale }
}

/**
 * Where the player's gaze meets the floor — the stand-in for a surface probe.
 *
 * Always answers. That is the point: the ritual's `aiming` phase exists only while there is
 * something under the reticle, so a source that can return "nothing" is a source that can leave
 * the player unable to place anything. A hit test against a real room can legitimately find
 * nothing; a floor plane always exists.
 *
 * The yaw is `yawFacing`, not the surface's dominant axis, because a floor plane has no dominant
 * axis. That makes this *better* than the probe on one axis: an MRUK hit reports the yaw of the
 * plane it landed on, which for a floor is arbitrary, while this turns the machine to the player.
 */
export function floorAim(
  headX: number,
  headY: number,
  headZ: number,
  fx: number,
  fy: number,
  fz: number,
  floorY = 0,
): SurfaceHit {
  const level = levelForward(fx, fz)
  const height = headY - floorY
  // Only a ray heading down, from above the floor, can meet it. A viewer *below* the floor plane
  // is mis-tracked or reading against an invented origin — exactly the case this file exists for
  // — and the fixed-distance path is the one that still gives them a machine they can reach.
  const descending = fy < -AIM_MIN_DOWN && height > 0
  let dx: number
  let dz: number
  if (descending) {
    const along = height / -fy
    dx = fx * along
    dz = fz * along
  } else {
    dx = level.x
    dz = level.z
  }
  // **Clamped horizontally, never along the ray.** A player looking at their own feet produces a
  // long ray whose horizontal offset is centimetres; clamping its length would leave the machine
  // on top of them. The distance that matters is the one measured on the floor.
  let reach = Math.hypot(dx, dz)
  if (!Number.isFinite(reach) || reach < 1e-6) {
    dx = level.x
    dz = level.z
    reach = 1
  }
  if (reach > AIM_MAX_DISTANCE) {
    const shrink = AIM_MAX_DISTANCE / reach
    dx *= shrink
    dz *= shrink
  }
  const x = headX + dx
  const z = headZ + dz
  return { x, y: floorY, z, yaw: yawFacing(x, z, headX, headZ) }
}

/**
 * The same placement, pulled to somewhere the player can touch it.
 *
 * Moves along the line between the player and the machine, so the direction the player chose is
 * preserved and only the distance changes — a machine placed to the left stays to the left. The
 * yaw is re-derived, because a machine that has been moved is facing the wrong way by exactly the
 * angle it moved through.
 *
 * **Height is not touched.** A cabinet standing on a table is a legitimate placement at any
 * distance and the reach question is a horizontal one; pulling a machine's `y` toward the player
 * would drag a tabletop cabinet off its table on the way past.
 *
 * Returns the placement unchanged when it is already in range, so this is safe to call on every
 * frame and on paths that were already correct.
 */
export function clampToReach(
  placement: Placement,
  headX: number,
  headZ: number,
  cabinetLength: number,
): Placement {
  const range = reachRange(placement.scale, cabinetLength)
  const dx = placement.x - headX
  const dz = placement.z - headZ
  const distance = Math.hypot(dx, dz)
  if (!Number.isFinite(distance)) return placement
  if (distance >= range.min && distance <= range.max) return placement
  // A machine standing exactly where the player is has no direction to be pushed along, so it
  // goes where a fresh placement would: straight ahead is unknowable from here, and the caller's
  // own yaw is the only heading on offer.
  let ux: number
  let uz: number
  if (distance < 1e-6) {
    ux = -Math.sin(placement.yaw)
    uz = Math.cos(placement.yaw)
    // The cabinet's front points at the player, so the player is *behind* it: to move the machine
    // away from the player, travel opposite its facing.
    ux = -ux
    uz = -uz
  } else {
    ux = dx / distance
    uz = dz / distance
  }
  const wanted = distance < range.min ? range.min : range.max
  const x = headX + ux * wanted
  const z = headZ + uz * wanted
  return { x, y: placement.y, z, yaw: yawFacing(x, z, headX, headZ, placement.yaw), scale: placement.scale }
}

/** Whether a placement is one the player could reach without moving. Used to explain, not to gate. */
export function withinReach(
  placement: Placement,
  headX: number,
  headZ: number,
  cabinetLength: number,
): boolean {
  const range = reachRange(placement.scale, cabinetLength)
  const distance = Math.hypot(placement.x - headX, placement.z - headZ)
  return distance >= range.min && distance <= range.max
}

/**
 * Whether the runtime's own surface probe is going to answer.
 *
 * Three states rather than two, because "not yet" and "never" need opposite responses and they
 * look identical from inside a frame callback. `requestHitTestSource` is a promise, so the first
 * second of every MR session has no source at all — substituting a floor plane there would put a
 * reticle under the player a moment before the real one arrives, and the machine would jump.
 */
export type ProbeState =
  /** The request is in flight. Wait: the real thing may be one frame away. */
  | 'pending'
  /** There is a source and it is being asked every frame. It may still find nothing. */
  | 'ready'
  /** There is no source and there will not be one — the feature was refused or is absent. */
  | 'refused'

/**
 * How long a working probe may find nothing before the floor plane takes over, seconds.
 *
 * 3. **A granted probe that never finds anything is the case that actually breaks people**, and
 * it is not the same as a refused one: on a headset with no saved Space Setup the runtime hands
 * out a perfectly good hit-test source and then reports no results for the rest of the session,
 * because it has no room to test against. Waiting for a refusal that never comes is how the
 * ritual stayed in `scanning` for an entire session.
 *
 * Long enough that a player glancing at the ceiling does not trip it, short enough that nobody
 * concludes the app is broken. The clock resets the instant a real hit arrives, so a room that
 * works is never overridden.
 */
export const PROBE_PATIENCE_SECONDS = 3

/** What the reticle should be drawn on this frame, and where the answer came from. */
export interface AimResult {
  readonly hit: SurfaceHit | undefined
  /** True when this is the floor plane standing in for a probe that is not going to answer. */
  readonly substituted: boolean
}

/**
 * The reticle's source of truth, whether or not the room exists.
 *
 * Held as an object rather than written as a function because the decision needs a memory: "this
 * probe has found nothing for three seconds" cannot be answered from one frame. One instance per
 * session, reset by constructing a new one, which is what both clients already do to the ritual.
 */
export class SurfaceSource {
  /** Seconds since the probe last found anything, while it was in a position to. */
  private dry = 0

  /** True once the floor plane has taken over, so the reticle does not flicker between sources. */
  private substituting = false

  /** Whether the answers being returned are coming from the floor plane rather than the room. */
  get fromFloorPlane(): boolean {
    return this.substituting
  }

  /**
   * One frame.
   *
   * **A real hit always wins, even after the substitute has taken over.** A player who walks into
   * a scanned part of their home, or whose headset finishes localising mid-session, should get
   * their real room back — and the room is a strictly better answer than a gaze ray, because it
   * knows about tables.
   */
  advance(
    probe: SurfaceHit | undefined,
    state: ProbeState,
    frameSeconds: number,
    headX: number,
    headY: number,
    headZ: number,
    fx: number,
    fy: number,
    fz: number,
    floorY = 0,
  ): AimResult {
    if (probe !== undefined) {
      this.dry = 0
      this.substituting = false
      return { hit: probe, substituted: false }
    }
    if (state === 'pending') {
      // Not evidence of anything. The source does not exist yet, so it has not failed to find
      // anything — and charging it for the time would substitute on the first frame of every
      // session, which is the one place the real answer is most likely to be a moment away.
      return { hit: undefined, substituted: false }
    }
    if (state === 'ready') {
      this.dry += frameSeconds
      if (!this.substituting && this.dry < PROBE_PATIENCE_SECONDS) {
        return { hit: undefined, substituted: false }
      }
    }
    this.substituting = true
    return {
      hit: floorAim(headX, headY, headZ, fx, fy, fz, floorY),
      substituted: true,
    }
  }
}
